import type { Icon } from "@phosphor-icons/react";
import { useSyncExternalStore } from "react";

/**
 * Command registry. The command palette, the `G then X` shortcuts and the shortcut sheet all
 * read from here, so a feature registers once and appears everywhere.
 *
 *   const off = registerCommands([{ id: "item.create", title: () => m.x(), group: () => m.y(), run: ... }]);
 *   // later: off()
 *
 * Titles are functions so they are evaluated at render time and follow live locale switches.
 * Registering an id that already exists replaces it until the newer registration is removed.
 */
export type CommandContext = { navigate: (to: string) => void };

export type Command = {
  id: string;
  title: () => string;
  group: () => string;
  icon?: Icon;
  /** Single-key sequence, e.g. ["g", "i"] or ["?"]. Disabled when single-key shortcuts are off. */
  keys?: string[];
  /** Extra search terms (already localised). */
  keywords?: () => string[];
  /** Hide the command when false (permissions, current route, selection). */
  when?: () => boolean;
  run: (ctx: CommandContext) => void;
};

type Entry = { token: symbol; command: Command };

let entries: Entry[] = [];
let snapshot: Command[] = [];
const listeners = new Set<() => void>();

function rebuild() {
  const byId = new Map<string, Command>();
  for (const e of entries) byId.set(e.command.id, e.command);
  snapshot = [...byId.values()];
  for (const l of listeners) l();
}

export function registerCommands(commands: Command[]): () => void {
  const token = Symbol("commands");
  entries = [...entries, ...commands.map((command) => ({ token, command }))];
  rebuild();
  return () => {
    entries = entries.filter((e) => e.token !== token);
    rebuild();
  };
}

export function getCommands(): Command[] {
  return snapshot;
}

export function useCommands(): Command[] {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => {
        listeners.delete(l);
      };
    },
    getCommands,
    getCommands,
  );
}

/* ---- overlay state shared between shell, shortcuts and palette ---- */

function createFlag() {
  let open = false;
  const subs = new Set<() => void>();
  const set = (v: boolean) => {
    if (open === v) return;
    open = v;
    for (const s of subs) s();
  };
  return {
    get: () => open,
    set,
    toggle: () => set(!open),
    subscribe: (l: () => void) => {
      subs.add(l);
      return () => {
        subs.delete(l);
      };
    },
  };
}

export const paletteFlag = createFlag();
export const shortcutsSheetFlag = createFlag();
export const drawerFlag = createFlag();

export function useFlag(flag: ReturnType<typeof createFlag>): boolean {
  return useSyncExternalStore(flag.subscribe, flag.get, flag.get);
}
